import {FormattedMessage} from '@pinecast/i18n';
import * as React from 'react';

import Button, {ButtonGroup} from './Button';
import Dialog, {Wash} from './Dialog';
import Layer, {CloseableLayer} from './Layer';
import {P} from './Text';
import type {LocalizedReactNode} from './types';

const ActResolver = Symbol('actresolver');
type Renderer = (resume: () => void, dismiss: () => void) => React.ReactNode;
type Act = {
  id: number;
  render: Renderer;
  [ActResolver]?: () => void;
};

const StageContext = React.createContext<{
  acts: Array<Act>;
  enqueue: (act: Act) => Promise<void>;
} | null>(null);

export const StageProvider = ({children}: {children: React.ReactNode}) => {
  const [acts, setActs] = React.useState<Array<Act>>([]);
  const enqueue = React.useCallback((act: Act) => {
    return new Promise<void>(resolve => {
      act[ActResolver] = resolve;
      setActs(acts => [...acts, act]);
    });
  }, []);
  const onDismiss = React.useCallback((act: Act) => {
    setActs(acts => acts.filter(x => x !== act));
  }, []);
  const value = React.useMemo(() => ({acts, enqueue}), [acts, enqueue]);
  return (
    <StageContext.Provider value={value}>
      {children}
      {acts.map(act => (
        <ActRender act={act} key={act.id} onDismiss={onDismiss} />
      ))}
    </StageContext.Provider>
  );
};

const useStage = () => {
  const payload = React.useContext(StageContext);
  if (!payload) {
    throw new Error('Stage provider was not found in render tree');
  }
  return payload;
};

const ActRender = React.memo(
  ({act, onDismiss}: {act: Act; onDismiss: (act: Act) => void}) => {
    const dismiss = React.useCallback(() => onDismiss(act), [onDismiss, act]);
    const resume = React.useCallback(() => {
      act[ActResolver]!();
      onDismiss(act);
    }, [onDismiss, act]);
    return (
      <React.Fragment key={act.id}>
        {act.render(resume, dismiss)}
      </React.Fragment>
    );
  },
);

let i = 0;
export function getActId(): number {
  return i++;
}

function wrapBody(node: React.ReactNode) {
  if (!React.isValidElement(node) || node.type !== FormattedMessage) {
    return node;
  }
  return <P>{node}</P>;
}

export const useAlertBox = () => {
  const {enqueue} = useStage();
  return React.useCallback(
    (
      title: LocalizedReactNode,
      body: React.ReactNode,
      continueButton: React.ReactNode = (
        <FormattedMessage
          description="Button to dismiss an alert in an affirmative way"
          defaultMessage="Okay"
        />
      ),
    ) => {
      return enqueue({
        id: getActId(),
        render(onContinue) {
          return (
            <CloseableLayer onClose={onContinue}>
              <Wash onClick={onContinue}>
                <Dialog
                  actions={
                    <Button autoFocus onClick={onContinue}>
                      {continueButton}
                    </Button>
                  }
                  size="small"
                  title={title}
                >
                  {wrapBody(body)}
                </Dialog>
              </Wash>
            </CloseableLayer>
          );
        },
      });
    },
    [enqueue],
  );
};

export const useConfirmBox = () => {
  const {enqueue} = useStage();
  return React.useCallback(
    (
      title: LocalizedReactNode,
      body: React.ReactNode,
      continueButton: React.ReactNode = (
        <FormattedMessage
          description="Button to dismiss an alert in an affirmative way"
          defaultMessage="Okay"
        />
      ),
      cancelButton: React.ReactNode = (
        <FormattedMessage
          description="Button to dismiss an alert in a negative way"
          defaultMessage="Cancel"
        />
      ),
    ) => {
      return enqueue({
        id: getActId(),
        render(onContinue, onDismiss) {
          return (
            <Layer>
              <Wash>
                <Dialog
                  actions={
                    <ButtonGroup>
                      <Button onClick={onDismiss}>{cancelButton}</Button>
                      <Button $isPrimary onClick={onContinue} autoFocus>
                        {continueButton}
                      </Button>
                    </ButtonGroup>
                  }
                  size="small"
                  title={title}
                >
                  {wrapBody(body)}
                </Dialog>
              </Wash>
            </Layer>
          );
        },
      });
    },
    [enqueue],
  );
};

export const useCustomModal = ({closable}: {closable?: boolean} = {}) => {
  const {enqueue} = useStage();
  const LayerComponent = closable ? CloseableLayer : Layer;
  return React.useCallback(
    (render: Renderer) =>
      enqueue({
        id: getActId(),
        render: (...args) => (
          <LayerComponent onClose={closable ? args[1] : (undefined as any)}>
            <Wash>{render(...args)}</Wash>
          </LayerComponent>
        ),
      }),
    [closable, enqueue, LayerComponent],
  );
};
